import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Bell, AlertTriangle, Pill, Activity, CheckCircle2, FileText, MessageCircle, Package, TrendingUp, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { PushNotificationManager } from '@/components/notifications/PushNotificationManager';

interface NotificationPreferences {
  task_executed: boolean;
  task_late: boolean;
  incident: boolean;
  sporadic_med: boolean;
  vital_alert: boolean;
  shift_summary: boolean;
  task_comment: boolean;
  comment_reply: boolean;
  low_stock: boolean;
  vital_trend: boolean;
  marketing_opt_in: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  task_executed: true,
  task_late: true,
  incident: true,
  sporadic_med: true,
  vital_alert: true,
  shift_summary: true,
  task_comment: true,
  comment_reply: true,
  low_stock: true,
  vital_trend: true,
  marketing_opt_in: false,
};

export default function NotificationSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading preferences:', error);
        toast.error(t('common.error'));
        return;
      }

      if (data) {
        setPreferences({
          task_executed: data.task_executed,
          task_late: data.task_late,
          incident: data.incident,
          sporadic_med: data.sporadic_med,
          vital_alert: data.vital_alert,
          shift_summary: data.shift_summary,
          task_comment: (data as any).task_comment ?? true,
          comment_reply: (data as any).comment_reply ?? true,
          low_stock: (data as any).low_stock ?? true,
          vital_trend: (data as any).vital_trend ?? true,
          marketing_opt_in: (data as any).marketing_opt_in ?? false,
        });
      } else {
        // No preferences exist yet, use defaults
        setPreferences(DEFAULT_PREFERENCES);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast.error(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving preferences:', error);
        toast.error(t('common.error'));
        return;
      }

      toast.success(t('common.success'));
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AppLayout title={t('settings.notifications')}>
        <div className="p-4 space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={t('settings.notifications')}
      showBack
      showFAB={false}
    >
      <div className="p-4 max-w-2xl mx-auto space-y-4">
        {/* Push Notification Manager */}
        <PushNotificationManager />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {t('settings.notificationTypes')}
            </CardTitle>
            <CardDescription>
              {t('settings.notificationTypesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Task Executed */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-success" />
                </div>
                <div>
                  <Label htmlFor="task_executed" className="font-medium">
                    {t('settings.notifyTaskExecuted')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyTaskExecutedDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="task_executed"
                checked={preferences.task_executed}
                onCheckedChange={() => handleToggle('task_executed')}
              />
            </div>

            {/* Task Late */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <Label htmlFor="task_late" className="font-medium">
                    {t('settings.notifyTaskLate')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyTaskLateDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="task_late"
                checked={preferences.task_late}
                onCheckedChange={() => handleToggle('task_late')}
              />
            </div>

            {/* Incident */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <Label htmlFor="incident" className="font-medium">
                    {t('settings.notifyIncident')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyIncidentDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="incident"
                checked={preferences.incident}
                onCheckedChange={() => handleToggle('incident')}
              />
            </div>

            {/* Sporadic Medication */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-sporadic/10 flex items-center justify-center">
                  <Pill className="w-5 h-5 text-sporadic" />
                </div>
                <div>
                  <Label htmlFor="sporadic_med" className="font-medium">
                    {t('settings.notifySporadicMed')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifySporadicMedDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="sporadic_med"
                checked={preferences.sporadic_med}
                onCheckedChange={() => handleToggle('sporadic_med')}
              />
            </div>

            {/* Vital Alert */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <Label htmlFor="vital_alert" className="font-medium">
                    {t('settings.notifyVitalAlert')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyVitalAlertDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="vital_alert"
                checked={preferences.vital_alert}
                onCheckedChange={() => handleToggle('vital_alert')}
              />
            </div>

            {/* Shift Summary */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="shift_summary" className="font-medium">
                    {t('settings.notifyShiftSummary')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyShiftSummaryDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="shift_summary"
                checked={preferences.shift_summary}
                onCheckedChange={() => handleToggle('shift_summary')}
              />
            </div>

            {/* Task Comment */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="task_comment" className="font-medium">
                    Comentários em tarefas
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notificar quando comentarem em tarefas que você executou
                  </p>
                </div>
              </div>
              <Switch
                id="task_comment"
                checked={preferences.task_comment}
                onCheckedChange={() => handleToggle('task_comment')}
              />
            </div>

            {/* Comment Reply */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="comment_reply" className="font-medium">
                    Respostas a comentários
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Notificar quando responderem aos seus comentários
                  </p>
                </div>
              </div>
              <Switch
                id="comment_reply"
                checked={preferences.comment_reply}
                onCheckedChange={() => handleToggle('comment_reply')}
              />
            </div>

            {/* Low Stock */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                  <Package className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <Label htmlFor="low_stock" className="font-medium">
                    {t('settings.notifyLowStock')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyLowStockDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="low_stock"
                checked={preferences.low_stock}
                onCheckedChange={() => handleToggle('low_stock')}
              />
            </div>

            {/* Vital Trend */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-warning" />
                </div>
                <div>
                  <Label htmlFor="vital_trend" className="font-medium">
                    {t('settings.notifyVitalTrend')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.notifyVitalTrendDesc')}
                  </p>
                </div>
              </div>
              <Switch
                id="vital_trend"
                checked={preferences.vital_trend}
                onCheckedChange={() => handleToggle('vital_trend')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Marketing opt-in */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Comunicações de marketing</CardTitle>
            <CardDescription>
              Novidades, dicas e promoções. Comunicações operacionais (alertas, convites, redefinição de senha) continuam sendo enviadas independentemente desta configuração.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="marketing_opt_in" className="font-medium">
                    Receber novidades por e-mail
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Você pode se descadastrar a qualquer momento.
                  </p>
                </div>
              </div>
              <Switch
                id="marketing_opt_in"
                checked={preferences.marketing_opt_in}
                onCheckedChange={() => handleToggle('marketing_opt_in')}
              />
            </div>
          </CardContent>
        </Card>

        <Button
          onClick={handleSave}
          className="w-full"
          size="lg"
          disabled={isSaving}
        >
          {isSaving ? t('common.loading') : t('common.save')}
        </Button>
      </div>
    </AppLayout>
  );
}